<template>
  <section class="bg-cream-100">
    <div class="section">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-end justify-between mb-5 sm:mb-8 gap-3 sm:gap-4">
        <div>
          <p class="text-brand font-semibold text-sm uppercase tracking-widest mb-1">Fresh & Ready</p>
          <h2 class="section-title mb-1">Today's Menu</h2>
          <p class="section-subtitle">
            {{ today }} — New dishes every day, made from scratch
          </p>
        </div>
        <router-link to="/menu" class="btn-outline text-sm self-start sm:self-auto">
          View Full Menu
          <ArrowRightIcon class="w-4 h-4" />
        </router-link>
      </div>

      <!-- Category filter -->
      <div class="flex items-center gap-2 overflow-x-auto scrollbar-hide mb-6 pb-1">
        <button
          v-for="cat in filterTabs"
          :key="cat"
          class="flex-shrink-0 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-all duration-150"
          :class="activeFilter === cat
            ? 'bg-brand text-white border-brand'
            : 'bg-white text-accent border-cream-200 hover:border-brand hover:text-brand'"
          @click="activeFilter = cat"
        >{{ cat }}</button>
      </div>

      <!-- Menu grid -->
      <div
        v-if="filteredItems.length"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
      >
        <MenuCard
          v-for="item in filteredItems"
          :key="item.id"
          :item="item"
          @add-to-cart="$emit('add-to-cart', $event)"
        />
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-20 text-accent/40">
        <p class="text-4xl mb-3">🍽️</p>
        <p class="font-semibold text-lg">No dishes in this category today</p>
        <p class="text-sm mt-1">Check back tomorrow or browse all dishes</p>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import MenuCard, { MenuItem } from './MenuCard.vue'

// Sample daily menu data — replace with API/store data later
const sampleMenu: MenuItem[] = [
  {
    id: 1,
    name: 'Isombe with Ugali',
    description: 'Cassava leaves slow-cooked with eggplant and served with ugali.',
    price: 2500,
    category: 'Stews',
    emoji: '🫕'
  },
  {
    id: 2,
    name: 'Grilled Chicken & Rice',
    description: 'Juicy marinated chicken grilled to perfection with steamed rice.',
    price: 4000,
    category: 'Grills',
    emoji: '🍖'
  },
  {
    id: 3,
    name: 'Beans & Plantain',
    description: 'Rwandan-style red beans with fried sweet plantain.',
    price: 2000,
    category: 'Rice',
    emoji: '🍚'
  },
  {
    id: 4,
    name: 'Vegetable Soup',
    description: 'Light, nourishing soup with seasonal vegetables and herbs.',
    price: 1500,
    category: 'Soups',
    emoji: '🥣'
  },
  {
    id: 5,
    name: 'Mixed Salad',
    description: 'Fresh garden salad with avocado, tomatoes, and lemon dressing.',
    price: 1800,
    category: 'Salads',
    emoji: '🥗'
  },
  {
    id: 6,
    name: 'Passion Fruit Juice',
    description: 'Freshly squeezed passion fruit, chilled and lightly sweetened.',
    price: 800,
    category: 'Drinks',
    emoji: '🥤'
  },
  {
    id: 7,
    name: 'Beef Stew & Potatoes',
    description: 'Tender beef in rich tomato sauce with boiled potatoes.',
    price: 4500,
    category: 'Stews',
    emoji: '🫕'
  },
  {
    id: 8,
    name: 'Mandazi',
    description: 'Sweet, fluffy East African doughnuts — perfect with tea.',
    price: 500,
    category: 'Desserts',
    emoji: '🍮'
  }
]

export default defineComponent({
  name: 'TodayMenuSection',
  components: { MenuCard, ArrowRightIcon },
  emits: ['add-to-cart'],
  setup () {
    const activeFilter = ref('All')

    const filterTabs = computed(() => {
      const cats = ['All', ...new Set(sampleMenu.map(i => i.category))]
      return cats
    })

    const filteredItems = computed(() =>
      activeFilter.value === 'All'
        ? sampleMenu
        : sampleMenu.filter(i => i.category === activeFilter.value)
    )

    const today = new Date().toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric'
    })

    return { activeFilter, filterTabs, filteredItems, today }
  }
})
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar { display: none; }
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
</style>
